import React, { useCallback, useEffect, useState } from 'react'
import FiltrosFacturacion, { FiltrosFac } from './FiltrosFacturacion'
import { consumidorFinalJob, getEstadosFacturacion, getJobs } from '../Vencimientos/contracts.services'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import { defaultPaginator, IPaginator } from '../../../components/Common/PaginatorServer'
import TablaJobs from './TablaJobs'
import { useMediaQuery, useTheme } from '@mui/material'
import CardsJobs from './CardsJobs'
import NoSearch from '../../../components/Common/NoSearch'
import NoResults from '../../../components/Common/NoResults'
import Navbar from '../../../components/Navbar'
import Footer from '../../Home/components/Footer'
import ConfirmDialog from '../../Products/components/ConfirmDialog'

const defaultFiltros: FiltrosFac = {
  idFacturacionJob: null,
  idContratoCtaCte: null,
  transactionId: null,
  numeroDocumento: null,
  cae: null,
  estado: null
}

export interface IJobs {
  idFacturacionJob: number
  idContratoCtaCte: number
  nombreContratoProducto: string
  estado: string
  reintentos: number
  error: string
  transactionId: number
  numeroDocumento: number
  cae: number
  nombreLocal: string
  codigoLocal: string
  fechaCreacion: string
  fechaActualizacion: string
  fechaInicioProceso: string
  tokenProceso: string
  procesadoPor: string
  forzarConsumidorFinal: boolean
}

const HEADERS = [
  { key: 'idFacturacionJob', label: 'Job', orden: true },
  { key: 'idContratoCtaCte', label: 'Contrato', orden: true },
  { key: 'transactionId', label: 'Transacción', orden: true },
  { key: 'nombreContratoProducto', label: 'Nombre Producto', orden: false },
  { key: 'estado', label: 'Estado', orden: true },
  { key: 'reintentos', label: 'Reintentos', orden: false },
  { key: 'numeroDocumento', label: 'Nro. Documento', orden: true },
  { key: 'cae', label: 'CAE', orden: true },
  { key: 'nombreLocal', label: 'Local', orden: false },
  { key: 'fechaCreacion', label: 'Fecha Creación', orden: true },
  { key: 'fechaActualizacion', label: 'Fecha Actualiz.', orden: false },
  { key: 'fechaInicioProceso', label: 'Inicio Proceso', orden: false },
  { key: 'error', label: 'Error', orden: false },
  { key: 'perfil-imp', label: '', orden: false }
]

const Facturacion: React.FC = () => {
  const [filtrosFac, setFiltrosFac] = useState<FiltrosFac>(defaultFiltros)
  const [loading, setLoading] = useState<boolean>(false)
  const [paginator, setPaginator] = useState<IPaginator>(defaultPaginator)
  const [estados, setEstados] = useState<
    {
      nombre: string
      id: number
    }[]
  >([])
  const [jobs, setJobs] = useState<IJobs[]>(null)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false
    }))
  }

  paginator.handleChangePage = (e: any, newPage: number) => {
    setPaginator({ ...paginator, pageNumber: newPage })
    fetchJobs(newPage, paginator.pageSize)
  }

  paginator.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value, 10)
    setPaginator({ ...paginator, pageSize: newPageSize, pageNumber: 0 })
    fetchJobs(0, newPageSize)
  }

  //FALTA MOSTRAR MSJ DE ÉXITO
  const handleConsumidorFinal = useCallback((idJob: number) => {
    setConfirmDialog({
      open: true,
      text: {
        title: 'Se facturará a consumidor final sin nombre',
        body: '¿Deseas continuar?',
        leftButton: 'Cancelar',
        rightButton: 'Confirmar',
        colorButtonConfirm: 'error'
      },
      action: async () => {
        try {
          setLoading(true)
          await consumidorFinalJob(idJob)
        } catch (e) {
          console.log(e)
        } finally {
          setLoading(false)
          clearConfirmDialog()
        }
      },
      close: () => clearConfirmDialog()
    })
  }, [])

  const fetchEstados = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getEstadosFacturacion()
      setEstados(res.data.data)
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchJobs = useCallback(
    async (pagina: number, size: number, orden?: { key: string; dir: string }) => {
      try {
        setLoading(true)
        const res = await getJobs(filtrosFac, pagina, size, orden)
        setJobs(res.data.data.results)
        setPaginator({
          ...paginator,
          pageNumber: res?.data.data.pageNumber,
          pageSize: res?.data.data.pageSize,
          totalPages: res?.data.data.totalPages,
          totalElements: res?.data.data.totalElements
        })
      } catch (e) {
        console.log(e)
        SnackBarUtils.error(e.message === 'Network Error' ? 'Ocurrió un error al obtener los jobs' : e.message)
      } finally {
        setLoading(false)
      }
    },
    [filtrosFac, paginator]
  )

  useEffect(() => {
    fetchEstados()
  }, [])

  return (
    <>
      <Navbar title="Facturación" />
      <FiltrosFacturacion
        loading={loading}
        isMobile={isMobile}
        estados={estados}
        filtros={filtrosFac}
        setFiltros={setFiltrosFac}
        handleFormSubmit={fetchJobs}
      />
      {jobs === null ? (
        <NoSearch label="Realice una búsqueda" />
      ) : jobs?.length === 0 ? (
        <NoResults />
      ) : isMobile ? (
        <CardsJobs
          headers={HEADERS}
          loading={loading}
          jobs={jobs}
          paginator={paginator}
          handleConsumidorFinal={handleConsumidorFinal}
        />
      ) : (
        <TablaJobs
          headers={HEADERS}
          loading={loading}
          jobs={jobs}
          paginator={paginator}
          fetchData={fetchJobs}
          handleConsumidorFinal={handleConsumidorFinal}
        />
      )}
      <Footer />
      <ConfirmDialog {...confirmDialog} />
    </>
  )
}

export default Facturacion
